/**
 * Analysis pipeline steps for the flowchart.
 * Each step maps to a progress event from the backend.
 */

export interface FlowchartStep {
  id: number;
  title: string;
  dataSource: string;
  estimatedDuration: string;
  phase: 'init' | 'phase1' | 'phase2' | 'phase3' | 'complete';
  progressMatchText?: string; // Substring to match in progress events
}

export const ANALYSIS_STEPS: FlowchartStep[] = [
  // INITIAL FETCH
  {
    id: 1,
    title: 'Fetch JIRA Ticket',
    dataSource: 'JIRA API v3 (with attachments)',
    estimatedDuration: '1-3s',
    phase: 'init',
    progressMatchText: 'Fetching JIRA',
  },

  // PHASE 1: STRUCTURED INTAKE
  {
    id: 2,
    title: 'Build Structured Text',
    dataSource: 'Ticket fields (title, description, status, etc.)',
    estimatedDuration: '0.5s',
    phase: 'phase1',
    progressMatchText: 'Building structured text',
  },

  {
    id: 3,
    title: 'Extract Text from Images (OCR)',
    dataSource: 'Gemini Vision API (up to 3 images)',
    estimatedDuration: '2-5s',
    phase: 'phase1',
    progressMatchText: 'Extracting text from image',
  },

  {
    id: 4,
    title: 'Merge Image Content',
    dataSource: 'Extracted text from screenshots',
    estimatedDuration: '0.5s',
    phase: 'phase1',
    progressMatchText: 'Merging image content',
  },

  {
    id: 5,
    title: 'Run AI Entity Extraction',
    dataSource: 'Claude AI + module catalogue',
    estimatedDuration: '2-4s',
    phase: 'phase1',
    progressMatchText: 'Running AI entity extraction',
  },

  {
    id: 6,
    title: 'Apply Analyst Hints',
    dataSource: 'User-confirmed modules/entities',
    estimatedDuration: '0.5s',
    phase: 'phase1',
    progressMatchText: 'Applying analyst hint',
  },

  {
    id: 7,
    title: 'Canonicalise Modules',
    dataSource: 'Neo4j module catalogue',
    estimatedDuration: '0.5s',
    phase: 'phase1',
    progressMatchText: 'Canonicalising modules',
  },

  {
    id: 8,
    title: 'Keyword Sweep Safety Net',
    dataSource: 'Hardcoded keyword map',
    estimatedDuration: '0.5s',
    phase: 'phase1',
    progressMatchText: 'Running keyword sweep',
  },

  {
    id: 9,
    title: 'Fetch Support Docs & Knowledge Bank',
    dataSource: 'BM25 + pgvector + vector search',
    estimatedDuration: '2-4s',
    phase: 'phase1',
    progressMatchText: 'Fetching Support Docs',
  },

  {
    id: 10,
    title: 'Generate Phase 1 Summary',
    dataSource: 'LLM synthesis from extracted context',
    estimatedDuration: '1-2s',
    phase: 'phase1',
    progressMatchText: 'Generating AI Summary from enriched',
  },

  // PHASE 2: DATA RETRIEVAL
  {
    id: 11,
    title: 'Phase 2: Data Retrieval',
    dataSource: 'ERAX/LRP + timelines + support docs',
    estimatedDuration: '5-15s',
    phase: 'phase2',
    progressMatchText: 'Phase 2: Retrieving data',
  },

  {
    id: 12,
    title: 'Normalise Entity Values',
    dataSource: 'Extracted entities',
    estimatedDuration: '1s',
    phase: 'phase2',
    progressMatchText: 'Phase 2: Normalising',
  },

  {
    id: 13,
    title: 'Plan SQL Queries',
    dataSource: 'Selected tables + entity bindings',
    estimatedDuration: '1-2s',
    phase: 'phase2',
    progressMatchText: 'Phase 2: Planning SQL',
  },

  {
    id: 14,
    title: 'Execute SQL & Generate Timelines',
    dataSource: 'LRP database queries + LLM synthesis',
    estimatedDuration: '3-8s',
    phase: 'phase2',
    progressMatchText: 'Phase 2 complete',
  },

  // PHASE 3: ANALYSIS
  {
    id: 15,
    title: 'Phase 3: Root Cause Analysis',
    dataSource: 'Hypotheses + SQL results + support docs',
    estimatedDuration: '3-8s',
    phase: 'phase3',
    progressMatchText: 'Phase 3: Running root cause',
  },

  // COMPLETION
  {
    id: 16,
    title: 'Prepare Final Analysis',
    dataSource: 'Phase 3 output',
    estimatedDuration: '1s',
    phase: 'phase3',
    progressMatchText: 'Phase 3 complete',
  },

  {
    id: 17,
    title: 'Save to Database',
    dataSource: 'PostgreSQL',
    estimatedDuration: '0.5s',
    phase: 'complete',
    progressMatchText: 'Saving analysis',
  },

  {
    id: 18,
    title: 'Analysis Complete',
    dataSource: 'All results compiled',
    estimatedDuration: '0s',
    phase: 'complete',
    progressMatchText: 'Analysis complete',
  },
];

/**
 * Map progress event text to step ID.
 * Returns the step ID if a match is found, otherwise null.
 */
export function mapProgressToStepId(progressText: string): number | null {
  for (const step of ANALYSIS_STEPS) {
    if (step.progressMatchText && progressText.includes(step.progressMatchText)) {
      return step.id;
    }
  }
  return null;
}
